import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useState } from "react";
import { toast } from "react-toastify";

import {
  activeHeldCart,
  addCustomToPosCart,
  addNoteToCartProduct,
  addToPosCart,
  clearCart,
  deleteHeldCart,
  getCartTotal,
  getPosCart,
  getPosCartCount,
  listOnHoldCarts,
  overrideProductPrice,
  removeFromCart,
  removePriceOverride,
  setCartStatusToHold,
  updateCartItem,
} from "../../core/cart.core";
import posInventoryCore from "../../core/inventory.core";
import { useTranslation } from "../../core/translation.core";
import { Cart, CartProduct, CustomCartItemData, EditCartProduct } from "../../types/cart";
import { Product } from "../../types/product";
import useRetailShop from "../useRetailShop";

export default function useCart() {
  const router = useRouter();
  const { locale, isReady } = router;
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();
  const retailShop = useRetailShop();

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleMessageReset = () => {
    setSuccessMessage("");
    setErrorMessage("");
  };

  const {
    data: cart,
    isLoading,
    isRefetching,
  } = useQuery({
    enabled: isReady && Boolean(retailShop),
    queryFn: async (context) => {
      const cartData = await getPosCart(context.queryKey[1]);
      //  @ts-ignore
      const transformedItems = await posInventoryCore.loadCartInventories(cartData.items, retailShop.warehouseIds);
      return { ...cartData, items: transformedItems };
    },
    queryKey: ["PosCart", locale],
  });

  const onError = (err, item, context) => {
    queryClient.setQueryData(["PosCart", locale], context.previousCart);
    toast.error(err.message);
    setErrorMessage(err);
  };

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["PosCart", locale] });
    queryClient.invalidateQueries({ queryKey: ["PosCartCount"] });
    queryClient.invalidateQueries({ queryKey: ["PosCartTotalPrice"] });
    // toast.success(t(msg.cart.productAddedToCart));
  };

  const { mutate: removeItem } = useMutation<void, void, { productId: string; cartItemId: string }, any>({
    mutationFn: removeFromCart,
    onError,
    onMutate: async ({ productId, cartItemId }) => {
      await queryClient.cancelQueries({ queryKey: ["PosCart", locale] });
      const previousCart = queryClient.getQueryData(["PosCart", locale]);

      queryClient.setQueryData(["PosCart", locale], (cart: Cart) => {
        const record = cart.items.filter(
          (i) => (i.product.id !== productId && i.discountLinkId !== productId) || i.attachCartItemId === cartItemId
        );
        return { ...cart, items: record };
      });

      return { previousCart };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["PosCart", locale] });
      queryClient.invalidateQueries({ queryKey: ["PosCartCount"] });
      queryClient.invalidateQueries({ queryKey: ["PosCartTotalPrice"] });
      toast.success(t(msg.cart.productRemovedFromCart));
    },
  });

  const {
    mutate: addItem,
    data: addItemData,
    isSuccess: addItemSuccess,
  } = useMutation<{ cartItemId: string }, void, CartProduct, any>({
    mutationFn: addToPosCart,
    onError,
    onMutate: async ({ product, quantity, warehouseId }) => {
      await queryClient.cancelQueries({ queryKey: ["PosCart", locale] });
      const previousCart = queryClient.getQueryData(["PosCart", locale]);
      // TODO: Take into account allow oversold to either allow or prevent adding product with no count

      queryClient.setQueryData(["PosCart", locale], (cart: Cart) => {
        return {
          ...cart,
          items: [
            ...cart.items,
            {
              inventories: product.inventories,
              itemPrice: product.price,
              product,
              quantity,
              retailInventories: product.retailInventories,
              warehouseId,
            },
          ],
        };
      });
      return { previousCart };
    },
    onSuccess,
  });

  const { mutate: addCustomItem } = useMutation<void, void, CustomCartItemData, any>({
    mutationFn: addCustomToPosCart,
    onError,
    onMutate: async ({ product, quantity, price, warehouseId }) => {
      await queryClient.cancelQueries({ queryKey: ["PosCart", locale] });
      const previousCart = queryClient.getQueryData(["PosCart", locale]);
      // TODO: Take into account allow oversold to either allow or prevent adding product with no count

      queryClient.setQueryData(["PosCart", locale], (cart: Cart) => {
        return {
          ...cart,
          items: [
            ...cart.items,
            {
              inventories: product.inventories,
              itemPrice: price,
              product,
              quantity,
              retailInventories: product.retailInventories,
              warehouseId,
            },
          ],
        };
      });
      return { previousCart };
    },
    onSuccess,
  });

  const { mutate: updateItem, isSuccess: updateItemSuccess } = useMutation<void, void, EditCartProduct, any>({
    mutationFn: updateCartItem,
    onError,
    onMutate: async ({ cartItemId, quantity }) => {
      await queryClient.cancelQueries({ queryKey: ["PosCart", locale] });
      const previousCart = queryClient.getQueryData(["PosCart", locale]);

      queryClient.setQueryData(["PosCart", locale], (cart: Cart) => {
        const record = cart.items.map((item) => (item.id === cartItemId ? { ...item, quantity } : item));
        return { ...cart, items: record };
      });
      return { previousCart };
    },
    onSuccess,
  });

  const { mutate: removeAllProducts } = useMutation<void, void, void, any>({
    mutationFn: clearCart,
    onError,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["PosCart", locale] });
      const previousCart = queryClient.getQueryData(["PosCart", locale]);

      queryClient.setQueryData(["PosCart", locale], (cart: Cart) => {
        return { ...cart, items: [] };
      });
      return { previousCart };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["PosCart", locale] });
      queryClient.invalidateQueries({ queryKey: ["PosCartCount"] });
      toast.success(t(msg.cart.cartCleared));
    },
  });

  const { data: cartCount } = useQuery({
    enabled: isReady,
    queryFn: () => getPosCartCount(),
    queryKey: ["PosCartCount"],
  });

  const { data: cartTotalPrice } = useQuery({
    enabled: isReady,
    queryFn: () => getCartTotal(),
    queryKey: ["PosCartTotalPrice"],
  });

  const updateCartStatusToHold = useMutation({
    mutationFn: async ({ customerId }: any) => {
      const result = setCartStatusToHold({ customerId });
      return result;
    },
    onError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["PosCart", locale] });
      queryClient.invalidateQueries({ queryKey: ["PosCartCount"] });
      queryClient.invalidateQueries({ queryKey: ["PosHeldCartList"] });
      toast.success(t(msg.cart.cartSetOnHold));
    },
  });

  const updateHeldCartStatusToActive = useMutation({
    mutationFn: async ({ customerId, cartId }: any) => {
      const result = activeHeldCart({ cartId, customerId });
      return result;
    },
    onError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["PosCart", locale] });
      queryClient.invalidateQueries({ queryKey: ["PosCartCount"] });
      queryClient.invalidateQueries({ queryKey: ["PosHeldCartList"] });
      toast.success(t(msg.cart.cartActivated));
    },
  });

  const deleteCart = useMutation({
    mutationFn: async (id: any) => {
      const result = deleteHeldCart(id);
      return result;
    },
    onError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["PosCart", locale] });
      queryClient.invalidateQueries({ queryKey: ["PosCartCount"] });
      queryClient.invalidateQueries({ queryKey: ["PosHeldCartList"] });
      toast.success(t(msg.cart.userCartDeleted));
    },
  });

  const listHeldCarts = useQuery({
    enabled: isReady,
    queryFn: () => listOnHoldCarts(),
    queryKey: ["PosHeldCartList"],
  });

  const getProductWarehouseId = (product?: Product) => {
    if (!product) {
      //  @ts-ignore
      return retailShop?.warehouseIds[0];
    }
    // TODO: Take into account allow oversold to either allow or prevent adding product with no count
    const warehouse = product?.retailInventories?.find((w) => w.count > 0);
    //  @ts-ignore
    return warehouse?.warehouseId || retailShop?.warehouseIds[0];
  };

  const overrideProductPriceMutation = useMutation<
    void,
    any,
    { price?: number; applyingDiscount?: number; discountedPrice?: number; discount?: number; productId: string },
    { previousCart: Cart | undefined }
  >({
    mutationFn: async ({ price, applyingDiscount, discountedPrice, productId }) => {
      return await overrideProductPrice({ price, applyingDiscount, discountedPrice, productId });
    },

    onError: (error, variables, context) => {
      if (context?.previousCart) {
        queryClient.setQueryData(["PosCart", locale], context.previousCart);
      }
      toast.error(error.message || "An error occurred while overriding the price");
    },

    onMutate: async ({ price, applyingDiscount, discountedPrice, discount, productId }) => {
      await queryClient.cancelQueries({ queryKey: ["PosCart", locale] });

      const previousCart = queryClient.getQueryData<Cart>(["PosCart", locale]);

      queryClient.setQueryData(["PosCart", locale], (cart: Cart | undefined) => {
        if (!cart) return cart;

        return {
          ...cart,
          items: cart.items.map((item) => {
            if (item.product.id === productId) {
              const originalPrice = item.itemPrice;

              const updatedPrice =
                discount !== undefined
                  ? originalPrice * (1 - discount / 100)
                  : applyingDiscount !== undefined
                    ? Math.max(0, originalPrice - applyingDiscount)
                    : discountedPrice !== undefined
                      ? discountedPrice
                      : price;

              const hasOverrideInput =
                price !== undefined ||
                applyingDiscount !== undefined ||
                discountedPrice !== undefined ||
                discount !== undefined;

              return {
                ...item,
                overridePrice: hasOverrideInput ? updatedPrice : item.overridePrice,
              };
            }
            return item;
          }),
        };
      });

      return { previousCart };
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["PosCart", locale] });
      queryClient.invalidateQueries({ queryKey: ["PosCartCount"] });
      queryClient.invalidateQueries({ queryKey: ["PosCartTotalPrice"] });
    },
  });

  const addNote = useMutation({
    mutationFn: async ({ productId, note }: any) => {
      const result = addNoteToCartProduct({ note, productId });
      return result;
    },
    onError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["PosCart", locale] });
      queryClient.invalidateQueries({ queryKey: ["PosCartCount"] });
      queryClient.invalidateQueries({ queryKey: ["PosCartTotalPrice"] });
    },
  });

  const removePriceOverrideMutation = useMutation({
    mutationFn: async ({ productId }: any) => {
      const result = removePriceOverride(productId);
      return result;
    },
    onError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["PosCart", locale] });
      queryClient.invalidateQueries({ queryKey: ["PosCartCount"] });
      queryClient.invalidateQueries({ queryKey: ["PosCartTotalPrice"] });
    },
  });

  return {
    addCustomItem,
    addItem,
    addItemData,
    addItemSuccess,
    addNote,
    cart,
    cartCount,
    cartTotalPrice,
    deleteCart,
    errorMessage,
    getProductWarehouseId,
    handleMessageReset,
    isRevalidating: isLoading || isRefetching,
    listHeldCarts,
    overrideProductPriceMutation,
    removeAllProducts,
    removeItem,
    removePriceOverrideMutation,
    setSuccessMessage,
    successMessage,
    updateCartStatusToHold,
    updateHeldCartStatusToActive,
    updateItem,
    updateItemSuccess,
  };
}
